import { useEffect, useState, useCallback, useRef } from 'react';
import { useToast, registerImperativeToast, type Toast } from '@/contexts/ToastContext';

const iconMap: Record<string, string> = {
  success: 'fas fa-check-circle',
  warning: 'fas fa-exclamation-triangle',
  error: 'fas fa-times-circle',
  info: 'fas fa-info-circle',
};

interface AnimatedToast extends Toast {
  phase: 'entering' | 'visible' | 'leaving';
}

export function ToastContainer() {
  const { toasts, addToast, removeToast } = useToast();
  const [animatedToasts, setAnimatedToasts] = useState<AnimatedToast[]>([]);
  const prevToastIdsRef = useRef<Set<number>>(new Set());

  // Register the imperative API so non-React code can show toasts
  useEffect(() => {
    registerImperativeToast(addToast);
  }, [addToast]);

  // Sync animated toasts with the toast context
  useEffect(() => {
    const currentIds = new Set(toasts.map((t) => t.id));
    const prevIds = prevToastIdsRef.current;

    // Find newly added toasts
    const added = toasts.filter((t) => !prevIds.has(t.id));
    // Find removed toast IDs
    const removedIds = new Set<number>();
    prevIds.forEach((id) => {
      if (!currentIds.has(id)) removedIds.add(id);
    });

    prevToastIdsRef.current = currentIds;

    setAnimatedToasts((prev) => {
      let next = prev.slice();

      // Mark removed toasts as 'leaving'
      if (removedIds.size > 0) {
        next = next.map((t) =>
          removedIds.has(t.id) ? { ...t, phase: 'leaving' as const } : t
        );
      }

      // Add new toasts as 'entering'
      for (const toast of added) {
        next.push({ ...toast, phase: 'entering' });
      }

      return next;
    });

    // Transition 'entering' → 'visible' on next frame
    if (added.length > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimatedToasts((prev) =>
            prev.map((t) =>
              t.phase === 'entering' ? { ...t, phase: 'visible' } : t
            )
          );
        });
      });
    }

    // Remove 'leaving' toasts after animation duration (300ms)
    if (removedIds.size > 0) {
      setTimeout(() => {
        setAnimatedToasts((prev) =>
          prev.filter((t) => !removedIds.has(t.id))
        );
      }, 300);
    }
  }, [toasts]);

  const handleDismiss = useCallback(
    (id: number) => {
      removeToast(id);
    },
    [removeToast]
  );

  const fullToasts = animatedToasts.filter((t) => !t.minimal);
  const minimalToasts = animatedToasts.filter((t) => t.minimal);

  if (fullToasts.length === 0 && minimalToasts.length === 0) return null;

  return (
    <>
      {/* Full toasts — top-right corner */}
      {fullToasts.length > 0 && (
        <div className="kst-toast-container kst-toast-container--top">
          {fullToasts.map((toast) => (
            <div
              key={toast.id}
              className={`kst-toast kst-toast--${toast.type} kst-toast--${toast.phase}`}
              role="alert"
              onClick={() => handleDismiss(toast.id)}
              style={{ cursor: 'pointer' }}
            >
              <i className={`${iconMap[toast.type] || iconMap.info} kst-toast__icon`} />
              <span
                className="kst-toast__msg"
                dangerouslySetInnerHTML={{ __html: toast.message }}
              />
              <button
                className="kst-toast__close"
                onClick={() => handleDismiss(toast.id)}
                aria-label="Dismiss"
                type="button"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Minimal toasts — bottom-center */}
      {minimalToasts.length > 0 && (
        <div className="kst-toast-container kst-toast-container--bottom">
          {minimalToasts.map((toast) => (
            <div
              key={toast.id}
              className={`kst-toast kst-toast--minimal kst-toast--${toast.type} kst-toast--${toast.phase}`}
              role="alert"
              onClick={() => handleDismiss(toast.id)}
              style={{ cursor: 'pointer' }}
            >
              <span className="kst-toast__msg">{toast.message}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
